<script setup lang="ts">
import type { CheckboxRootEmits, CheckboxRootProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { Check } from '@lucide/vue'
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'reka-ui'
import { cn } from '@ds/lib/utils'

const props = defineProps<CheckboxRootProps & { class?: HTMLAttributes['class'] }>()
const emits = defineEmits<CheckboxRootEmits>()

const delegatedProps = reactiveOmit(props, 'class')

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <CheckboxRoot
    v-bind="forwarded"
    :class="
      cn('grid place-content-center peer shrink-0',
         // 20px at radius-sm, per the canonical component — shadcn ships 16px.
         'size-5 rounded-ds-sm',
         // 1.5px, also canonical. An unchecked box has no fill and no glyph, so
         // its border IS the control: `border-strong` is the step that clears
         // WCAG 1.4.11's 3:1 for it. See ds-tokens.css.
         'border-[1.5px] border-strong bg-card',
         'transition-[background-color,border-color] duration-ds-fast ease-ds-standard',
         'focus-visible:outline-none focus-visible:shadow-ds-focus',
         'disabled:cursor-not-allowed disabled:opacity-50',
         'data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground',
         'cursor-pointer',
         props.class)"
  >
    <CheckboxIndicator class="grid place-content-center text-current">
      <slot>
        <!-- Canonical draws the tick at 3.5 stroke; Lucide's default is 2. -->
        <Check class="size-3.5 stroke-[3.5]" />
      </slot>
    </CheckboxIndicator>
  </CheckboxRoot>
</template>
